import { selectStandardProps } from "@/utils/componentUtils";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Checkbox, Col, Form, Input, Row, Select } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, Link, useNavigate } from "react-router";
import AntIcon from "../../../../common/components/icons/AntIcon";
import { rowGutter } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import ComponentWithPermission from "../../../auth/components/ComponentWithPermission";
import { ProductFinancialSector } from "../../../product/enums";
import { FINANCIAL_MEDIATION_ROUTE_PATHS } from "../../paths";
import { FinancialMediationFilterPageRequest } from "../../types";

const colSpan = 6;

const initialFilterFormValues: Partial<Record<keyof FinancialMediationFilterPageRequest, any>> = {
  includeAssigned: false,
};

type Props = {
  searchParams?: FinancialMediationFilterPageRequest;
};

export const FinancialMediationFilterView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<FinancialMediationFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue({
      keyword: searchParams?.keyword,
      sectors: searchParams?.sectors || [],
      includeAssigned: !!searchParams?.includeAssigned,
    });
  }, [searchParams, form]);

  const handleFilterSubmit = (filter: FinancialMediationFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: { ...filter, ...skipInitialFormValues(filter, initialFilterFormValues), pageIndex: undefined },
        }),
      },
      { replace: true },
    );
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("financialMediation.titles.list")}</h2>}
      extra={
        <ComponentWithPermission
          permissions={[
            Permission.INSURANCE_CREATE,
            Permission.LOAN_CREATE,
            Permission.DEPOSIT_CREATE,
            Permission.SECOND_PILLAR_CREATE,
            Permission.THIRD_PILLAR_CREATE,
          ]}
          anyPermissionPresent
        >
          <Link to={generatePath(FINANCIAL_MEDIATION_ROUTE_PATHS.create.to)}>
            <Button type="primary" icon={<AntIcon type="plus" />}>
              {t("financialMediation.actions.createMediation")}
            </Button>
          </Link>
        </ComponentWithPermission>
      }
    >
      <Form form={form} onFinish={handleFilterSubmit} layout="vertical" name="financialMediationFilterForm">
        <Row gutter={rowGutter} justify="center">
          <Col span={colSpan}>
            <Form.Item
              name="keyword"
              label={t("common.searchKeyword")}
              rules={[
                validations.size(
                  validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                  validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
                ),
              ]}
            >
              <Input allowClear placeholder={t("financialMediation.helpers.searchHint")} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item name="sectors" label={t("product.enums.financialSector._label")} rules={[validations.none]}>
              <Select
                {...selectStandardProps}
                allowClear
                mode="multiple"
                maxTagCount="responsive"
                options={Object.keys(ProductFinancialSector)
                  .filter(
                    (sector) =>
                      sector !== ProductFinancialSector.CAPITAL_MARKET &&
                      sector !== ProductFinancialSector.OTHERS &&
                      sector !== ProductFinancialSector.ALL,
                  )
                  .map((sector) => ({
                    value: sector,
                    label: t("product.enums.financialSector." + sector),
                  }))}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan - 2}>
            <Form.Item
              name="includeAssigned"
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={true}
            >
              <Checkbox>{t("financialMediation.filter.includeAssigned")}</Checkbox>
            </Form.Item>
          </Col>

          <Col flex="110px" className="no-title-space">
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
                {t("common.filter")}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
